// All gameplay tuning lives here as data. Units: metres, seconds, kilograms, radians.
// Values are first-pass and tuned against headless tests and real play.

export const SIM = {
  tickRateHz: 60,
  /**
   * Bump whenever physics, terrain or run rules change: ghosts recorded under another version are
   * discarded, because their inputs would no longer replay to the same run.
   */
  version: 6,
  /** Half-depth of the terrain slab along Z; the buggy drives on z = 0. */
  terrainHalfDepth: 1.5,
} as const;

export const BUGGY = {
  spawnX: 6,
  chassis: {
    halfLength: 1.15,
    halfHeight: 0.22,
    halfDepth: 0.55,
    /** ≈ 200 kg chassis; the vehicle controller's suspension constants scale with chassis mass. */
    density: 180,
    friction: 0.6,
    /** Spawn height of the chassis centre above the ground. */
    spawnClearance: 1.0,
  },
  helmet: {
    radius: 0.27,
    /** Offset from the chassis centre, in chassis space. */
    offset: { x: -0.15, y: 0.78 },
  },
  wheel: {
    radius: 0.42,
    halfWidth: 0.22,
    /** Higher = more grip before the tyre slips. */
    frictionSlip: 6,
    /** Axle positions along the chassis (rear, front). */
    axleX: [-0.92, 0.95],
    /** Axle offset below the chassis centre at rest. */
    axleY: -0.48,
  },
  suspension: {
    restLength: 0.35,
    travel: 0.22,
    /** Per unit chassis mass (Bullet-style ray-cast vehicle constants). */
    stiffness: 45,
    compression: 3.2,
    relaxation: 4.2,
    maxForce: 60000,
    /** Bump stops: chassis spheres where a fully compressed wheel would be. */
    bumpStopRadiusScale: 0.85,
    bumpStopFriction: 0.4,
  },
  drive: {
    /** Forward acceleration at full throttle from standstill (m/s²). */
    acceleration: 6.5,
    topSpeed: 12.5,
    brake: 40,
    /** Reverse is allowed below this forward speed (m/s). */
    reverseBelowSpeed: 0.8,
    reverseTopSpeed: 4,
  },
  air: {
    /** Pitch acceleration at full input while airborne (rad/s²); about one flip per 1.1 s. */
    pitchAccel: 9,
    maxPitchRate: 6.5,
  },
} as const;

export const FUEL = {
  capacity: 100,
  drainIdle: 0.4,
  drainThrottle: 1.6,
  canAmount: 50,
  /** Stalled: fuel empty and speed below this for stallSeconds. */
  stallSpeed: 0.3,
  stallSeconds: 2,
} as const;

export const STUNTS = {
  flipCoins: 15,
  flipFuel: 6,
  /** Airtime below this earns nothing. */
  airtimeThreshold: 1.5,
  airtimeCoinsPerHalfSecond: 5,
  /** Minimum airborne time before a contact-free period counts as "air" at all. */
  airborneGraceSeconds: 0.12,
} as const;

export const PICKUPS = {
  collectRadius: 1.25,
  coinValue: 1,
} as const;

export const CAMERA = {
  fov: 32,
  distance: 23,
  height: 2.2,
  lookAheadMax: 0.35, // fraction of view width
  lookAheadFullSpeed: 12,
  zoomOutAir: 0.2,
  followLerp: 6,
  portraitDistanceScale: 1.65,
} as const;

/** Meals delivered: a coin bonus each time the run passes another `every` metres. */
export const DELIVERY = {
  every: 500,
  coins: 20,
} as const;

/** Garage upgrades: per-level effects and the price of each level (Phase 3, docs/design/06). */
export const UPGRADES = {
  maxLevel: 5,
  costs: [30, 60, 110, 180, 280],
  engine: { accelerationPerLevel: 0.08, topSpeedPerLevel: 0.05 },
  tyres: { gripPerLevel: 0.12 },
  suspension: { travelPerLevel: 0.025, dampingPerLevel: 0.06 },
  /** Percent of the vehicle's base tank added per level. */
  tank: { capacityPerLevel: 20 },
} as const;
